import { z } from 'zod';
/**
 * Schemas do PDF parseado do LinkedIn.
 *
 * Esses tipos circulam entre web e api via @smartapply/shared. O servidor
 * gera, valida e mantém em memória durante a sessão. Nada disso persiste
 * em banco — apenas métricas anônimas derivadas.
 */
export const ContactSchema = z.object({
    hasMobile: z.boolean(),
    hasEmail: z.boolean(),
    hasPortfolio: z.boolean(),
    portfolioUrls: z.array(z.string()).default([]),
});
export const ExperienceSchema = z.object({
    company: z.string().min(1),
    title: z.string().min(1),
    startDate: z.string(),
    endDate: z.string().nullable(),
    durationMonths: z.number().int().nullable(),
    location: z.string().nullable(),
    description: z.string(),
});
export const CertificationSchema = z.object({
    name: z.string().min(1),
    issuer: z.string().nullable(),
});
export const EducationSchema = z.object({
    institution: z.string().min(1),
    degree: z.string().nullable(),
    field: z.string().nullable(),
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
});
export const FeaturedItemSchema = z.object({
    title: z.string(),
    description: z.string().nullable(),
    kind: z.enum(['post', 'article', 'link', 'media', 'unknown']).default('unknown'),
});
export const ServiceItemSchema = z.object({
    name: z.string(),
});
export const RecommendationSchema = z.object({
    authorName: z.string(),
    authorTitle: z.string().nullable(),
    relationship: z.string().nullable(),
    text: z.string(),
});
export const VolunteerSchema = z.object({
    organization: z.string(),
    role: z.string().nullable(),
    cause: z.string().nullable(),
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    description: z.string().nullable(),
});
export const LanguageSchema = z.object({
    name: z.string(),
    proficiency: z.string().nullable(),
});
export const PublicationSchema = z.object({
    title: z.string(),
    publisher: z.string().nullable(),
    date: z.string().nullable(),
    description: z.string().nullable(),
});
export const ProjectSchema = z.object({
    name: z.string(),
    description: z.string().nullable(),
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
});
export const HonorAwardSchema = z.object({
    title: z.string(),
    issuer: z.string().nullable(),
    date: z.string().nullable(),
    description: z.string().nullable(),
});
export const CourseSchema = z.object({
    name: z.string(),
    number: z.string().nullable(),
});
export const TestScoreSchema = z.object({
    name: z.string(),
    score: z.string().nullable(),
    date: z.string().nullable(),
});
export const OrganizationSchema = z.object({
    name: z.string(),
    position: z.string().nullable(),
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    description: z.string().nullable(),
});
/**
 * Seção custom que o LinkedIn permite criar livremente, ou que o parser
 * não reconheceu como padrão. Guardamos title + content bruto pra LLM
 * conseguir avaliar mesmo sem schema dedicado.
 */
export const CustomSectionSchema = z.object({
    title: z.string(),
    content: z.string(),
});
export const ParserWarningSchema = z.object({
    code: z.string(),
    message: z.string(),
    field: z.string().nullable(),
});
export const LinkedInProfileSchema = z.object({
    fullName: z.string().min(1),
    headline: z.string(),
    summary: z.string(),
    location: z.string().nullable(),
    contact: ContactSchema,
    // topSkills: Top 3 que o LinkedIn pina abaixo da foto (UI exibida em buscas).
    topSkills: z.array(z.string()).max(3),
    // allSkills: lista completa que aparece na seção Skills do perfil. Importante
    // pra ATS/Recruiter Search, que faz match em qualquer skill listada, não só nas top 3.
    allSkills: z.array(z.string()).default([]),
    experiences: z.array(ExperienceSchema),
    certifications: z.array(CertificationSchema),
    educations: z.array(EducationSchema),
    // Seções extras que o LinkedIn suporta — todas opcionais; default []
    featured: z.array(FeaturedItemSchema).default([]),
    services: z.array(ServiceItemSchema).default([]),
    recommendations: z.array(RecommendationSchema).default([]),
    volunteer: z.array(VolunteerSchema).default([]),
    languages: z.array(LanguageSchema).default([]),
    publications: z.array(PublicationSchema).default([]),
    projects: z.array(ProjectSchema).default([]),
    honorsAwards: z.array(HonorAwardSchema).default([]),
    courses: z.array(CourseSchema).default([]),
    testScores: z.array(TestScoreSchema).default([]),
    organizations: z.array(OrganizationSchema).default([]),
    // Seções custom do user OU seções que o parser detectou mas não reconheceu.
    // O LLM lê title + content e analisa do mesmo jeito.
    customSections: z.array(CustomSectionSchema).default([]),
    // Texto bruto extraído do PDF inteiro (fallback de última instância).
    // O LLM recebe junto pra não perder nada que o parser tenha errado em
    // segmentar. Limitado a 32k chars no parser pra controlar custo do LLM.
    rawText: z.string().default(''),
    warnings: z.array(ParserWarningSchema).default([]),
    meta: z.object({
        pdfBytes: z.number().int(),
        pdfPages: z.number().int(),
        parsedAt: z.string(),
        locale: z.string().nullable(),
    }),
});
/**
 * Schema do que devolvemos no /upload — preview (sem conteúdo bruto).
 */
export const UploadPreviewSchema = z.object({
    sessionId: z.string().uuid(),
    snapshotId: z.string().uuid(),
    expiresAt: z.string(),
    // Profile completo retornado pra o client guardar em sessionStorage. Se o
    // byokStore do server perder o snapshot (restart ou TTL), o client envia
    // o profile de volta nas próximas chamadas, evitando reupload do PDF.
    profile: LinkedInProfileSchema.optional(),
    preview: z.object({
        fullName: z.string(),
        headlineLength: z.number().int(),
        summaryLength: z.number().int(),
        sectionsDetected: z.object({
            headline: z.boolean(),
            summary: z.boolean(),
            experiencesCount: z.number().int(),
            skillsTop3Count: z.number().int(),
            allSkillsCount: z.number().int().default(0),
            certificationsCount: z.number().int(),
            educationsCount: z.number().int(),
            featuredCount: z.number().int().default(0),
            servicesCount: z.number().int().default(0),
            recommendationsCount: z.number().int().default(0),
            volunteerCount: z.number().int().default(0),
            languagesCount: z.number().int().default(0),
            publicationsCount: z.number().int().default(0),
            projectsCount: z.number().int().default(0),
            honorsAwardsCount: z.number().int().default(0),
            coursesCount: z.number().int().default(0),
            testScoresCount: z.number().int().default(0),
            organizationsCount: z.number().int().default(0),
            customSectionsCount: z.number().int().default(0),
        }),
        warnings: z.array(ParserWarningSchema),
    }),
});
//# sourceMappingURL=pdf-snapshot.js.map